import {
  ActionIcon,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuArrowDownToLine,
  LuArrowUpFromLine,
  LuChevronLeft,
  LuChevronRight,
  LuClock,
  LuPlane,
  LuSearch,
} from 'react-icons/lu'
import { CustomerAvatar } from '@/frontend/components/marina/CustomerAvatar'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { useArrivalsDepartures } from '@/frontend/hooks/useOccupancy'

type TabValue = 'all' | 'arrivals' | 'departures'

export const Route = createFileRoute('/dashboard/occupancy/arrivals')({
  validateSearch: (s: Record<string, unknown>): { tab?: TabValue } => ({
    tab: ['all', 'arrivals', 'departures'].includes(s.tab as string) ? (s.tab as TabValue) : undefined,
  }),
  component: ArrivalsPage,
})

function ArrivalsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'all'
  const setTab = (v: TabValue) => navigate({ search: () => ({ tab: v }), replace: true })
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [search, setSearch] = useState('')

  const { data, isLoading } = useArrivalsDepartures({ date, type: tab })
  const arrivals = data?.arrivals ?? []
  const departures = data?.departures ?? []

  const isToday = date === dayjs().format('YYYY-MM-DD')
  const isFuture = dayjs(date).isAfter(dayjs(), 'day')

  const all = [...arrivals, ...departures]
    .filter((row) => {
      if (!search) return true
      const s = search.toLowerCase()
      return (
        row.code.toLowerCase().includes(s) ||
        `${row.customer.firstName} ${row.customer.lastName}`.toLowerCase().includes(s) ||
        row.boat.name.toLowerCase().includes(s) ||
        (row.berth?.code ?? '').toLowerCase().includes(s)
      )
    })
    .sort((a, b) => dayjs(a.scheduledAt).valueOf() - dayjs(b.scheduledAt).valueOf())

  // Stats
  const delayedCount = all.filter((r) => {
    if (!r.actualAt) return false
    return dayjs(r.actualAt).diff(dayjs(r.scheduledAt), 'minute') > 30
  }).length
  const onTimeCount = all.filter((r) => r.actualAt).length

  const prevDay = () => setDate(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'))
  const nextDay = () => setDate(dayjs(date).add(1, 'day').format('YYYY-MM-DD'))

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuPlane size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.occupancy.arrivals.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {isToday ? 'Hari ini' : isFuture ? 'Mendatang — ' : 'Arsip — '}
                {dayjs(date).format('dddd, DD MMM YYYY')}
              </Text>
            </div>
          </Group>
        </Group>

        {/* Stats strip */}
        {!isLoading && all.length > 0 && (
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuArrowDownToLine size={13} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.arrivals.stat_arrivals')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {arrivals.length}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="karang" size="sm" radius="sm">
                  <LuArrowUpFromLine size={13} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.arrivals.stat_departures')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {departures.length}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs" wrap="nowrap">
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.arrivals.stat_recorded')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {onTimeCount} / {all.length}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs" wrap="nowrap">
                <Text size="xs" c={delayedCount > 0 ? 'red.6' : 'dimmed'}>
                  {t('page.occupancy.arrivals.stat_late')}
                </Text>
                <Text size="xs" fw={700} ml="auto" c={delayedCount > 0 ? 'red.6' : undefined}>
                  {delayedCount}
                </Text>
              </Group>
            </Card>
          </SimpleGrid>
        )}

        {/* Controls */}
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="sm" wrap="wrap">
            <PillNav
              value={tab}
              onChange={setTab}
              items={[
                {
                  value: 'all',
                  label: t('page.occupancy.arrivals.tab_all'),
                  count: arrivals.length + departures.length || undefined,
                  countColor: 'gray',
                },
                {
                  value: 'arrivals',
                  label: t('page.occupancy.arrivals.stat_arrivals'),
                  icon: <LuArrowDownToLine size={12} />,
                  count: arrivals.length,
                  countColor: 'laut',
                },
                {
                  value: 'departures',
                  label: t('page.occupancy.arrivals.stat_departures'),
                  icon: <LuArrowUpFromLine size={12} />,
                  count: departures.length,
                  countColor: 'karang',
                },
              ]}
            />
          </Group>

          <Group gap="sm" wrap="wrap">
            <TextInput
              placeholder={t('page.occupancy.arrivals.placeholder')}
              leftSection={<LuSearch size={13} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              size="xs"
              w={220}
            />
            <Group gap={4}>
              <Tooltip label="Hari sebelumnya" withArrow>
                <ActionIcon variant="default" size="sm" onClick={prevDay}>
                  <LuChevronLeft size={15} />
                </ActionIcon>
              </Tooltip>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  fontSize: '0.8rem',
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--app-card-bg)',
                  color: 'inherit',
                }}
              />
              <Tooltip label="Hari berikutnya" withArrow>
                <ActionIcon variant="default" size="sm" onClick={nextDay}>
                  <LuChevronRight size={15} />
                </ActionIcon>
              </Tooltip>
              {!isToday && (
                <ActionIcon
                  variant="light"
                  color="laut"
                  size="sm"
                  onClick={() => setDate(dayjs().format('YYYY-MM-DD'))}
                  title="Hari ini"
                >
                  <LuClock size={13} />
                </ActionIcon>
              )}
            </Group>
          </Group>
        </Group>

        {/* Table */}
        {isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 6 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton count
              <Skeleton key={i} height={60} radius="md" />
            ))}
          </Stack>
        ) : (
          <Card radius="md" padding="lg">
            <DataTable
              isEmpty={all.length === 0}
              emptyLabel={
                search
                  ? t('page.occupancy.arrivals.no_results', { q: search })
                  : `Tidak ada ${tab === 'arrivals' ? 'kedatangan' : tab === 'departures' ? 'keberangkatan' : 'aktivitas'} pada tanggal ini`
              }
              page={1}
              pageSize={100}
              total={all.length}
              columns={
                <>
                  <Table.Th w={28} />
                  <Table.Th>Kapal & Pelanggan</Table.Th>
                  <Table.Th>Berth</Table.Th>
                  <Table.Th>Jadwal</Table.Th>
                  <Table.Th>Aktual</Table.Th>
                  <Table.Th ta="right">Booking</Table.Th>
                </>
              }
            >
              {all.map((row) => {
                const name = `${row.customer.firstName} ${row.customer.lastName}`.trim()
                const isArrival = row.kind === 'arrival'
                const delayMin = row.actualAt ? dayjs(row.actualAt).diff(dayjs(row.scheduledAt), 'minute') : null
                const isDelayed = delayMin !== null && delayMin > 30
                const isEarly = delayMin !== null && delayMin < -15

                return (
                  <Table.Tr
                    key={`${row.id}-${row.kind}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate({ to: `/dashboard/reservations/${row.id}` as any })}
                  >
                    {/* Kind indicator */}
                    <Table.Td>
                      <Tooltip
                        label={
                          isArrival
                            ? t('page.occupancy.arrivals.tooltip_arrival')
                            : t('page.occupancy.arrivals.tooltip_departure')
                        }
                        withArrow
                        position="right"
                      >
                        <ThemeIcon variant="light" color={isArrival ? 'laut' : 'karang'} size="sm" radius="xl">
                          {isArrival ? <LuArrowDownToLine size={12} /> : <LuArrowUpFromLine size={12} />}
                        </ThemeIcon>
                      </Tooltip>
                    </Table.Td>

                    {/* Boat + Customer */}
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <CustomerAvatar name={name} email={row.customer.email} size={34} />
                        <Stack gap={1}>
                          <Text size="sm" fw={600}>
                            {row.boat.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {name}
                          </Text>
                          <Group gap={4}>
                            <KindBadge category={row.customer.category} />
                            {row.boat.type && (
                              <Badge size="xs" variant="light" color="gray">
                                {row.boat.type}
                              </Badge>
                            )}
                          </Group>
                        </Stack>
                      </Group>
                    </Table.Td>

                    {/* Berth */}
                    <Table.Td>
                      {row.berth ? (
                        <Badge variant="light" color="laut" size="sm" radius="sm">
                          {row.berth.code}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>

                    {/* Scheduled */}
                    <Table.Td>
                      <Text size="sm">{dayjs(row.scheduledAt).format('HH:mm')}</Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(row.scheduledAt).format('DD MMM')}
                      </Text>
                    </Table.Td>

                    {/* Actual */}
                    <Table.Td>
                      {row.actualAt ? (
                        <Stack gap={1}>
                          <Text
                            size="sm"
                            fw={isDelayed ? 600 : 400}
                            c={isDelayed ? 'red.6' : isEarly ? 'teal.6' : undefined}
                          >
                            {dayjs(row.actualAt).format('HH:mm')}
                          </Text>
                          {isDelayed && (
                            <Badge size="xs" color="red" variant="light">
                              +{Math.round(delayMin / 60)}j {delayMin % 60}m
                            </Badge>
                          )}
                          {isEarly && (
                            <Badge size="xs" color="teal" variant="light">
                              lebih awal
                            </Badge>
                          )}
                        </Stack>
                      ) : (
                        <Text size="xs" c="dimmed">
                          {isFuture ? 'Terjadwal' : 'Belum tercatat'}
                        </Text>
                      )}
                    </Table.Td>

                    {/* Booking link */}
                    <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                      <Anchor
                        component={Link}
                        to={`/dashboard/reservations/${row.id}` as any}
                        c="laut"
                        size="sm"
                        fw={500}
                      >
                        {row.code} →
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </DataTable>
          </Card>
        )}
      </Stack>
    </Container>
  )
}
